import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface SlideImages {
  landscape: string;
  portrait: string;
}

const SLIDES: SlideImages[] = [
  { landscape: '/opening-story/opening-1-1.png', portrait: '/opening-story/opening-1-2.png' },
  { landscape: '/opening-story/opening-2-1.png', portrait: '/opening-story/opening-2-2.png' },
  { landscape: '/opening-story/opening-4-1.png', portrait: '/opening-story/opening-4-2.png' },
];

const SLIDE_CAPTIONS: Record<number, string> = {
  0: 'Earth Guardians come from universe. They bring letters to humans.',
  1: 'You receive a letter from Earth Guardians.',
};

const GameOpening: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const nextCharacterSelectUrl = `/game/character-select?${searchParams.toString()}`;

  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(true);

  const goToCharacterSelect = useCallback(() => {
    navigate(nextCharacterSelectUrl);
  }, [navigate, nextCharacterSelectUrl]);

  const advance = useCallback(() => {
    if (current >= SLIDES.length - 1) {
      goToCharacterSelect();
      return;
    }
    setVisible(false);
    setTimeout(() => {
      setCurrent((c) => c + 1);
      setVisible(true);
    }, 300);
  }, [current, goToCharacterSelect]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Enter') advance();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [advance]);

  return (
    <div
      className="relative flex h-screen w-full items-center justify-center bg-black cursor-pointer select-none"
      onClick={advance}
    >
      <picture key={current}>
        <source media="(orientation: portrait)" srcSet={SLIDES[current].portrait} />
        <img
          src={SLIDES[current].landscape}
          alt={`Opening screen ${current + 1}`}
          className="h-full w-full object-cover"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
        />
      </picture>

      {SLIDE_CAPTIONS[current] && (
        <div className="absolute inset-x-0 top-[18%] flex justify-center px-6 pointer-events-none opening-caption-wrapper">
          <p
            key={current}
            className="text-center max-w-2xl opening-caption-text"
            style={{
              fontFamily: "'Dancing Script', cursive",
              fontSize: 'clamp(28px, 5vw, 50px)',
              color: '#FFFFFF',
              textShadow: '0 2px 10px rgba(0,0,0,0.75), 0 0 22px rgba(0,0,0,0.5)',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0px)' : 'translateY(16px)',
              transition: 'opacity 900ms ease-out 250ms, transform 900ms ease-out 250ms',
            }}
          >
            {SLIDE_CAPTIONS[current]}
          </p>
        </div>
      )}

      {/* skip button */}
      <button
        onClick={(e) => { e.stopPropagation(); goToCharacterSelect(); }}
        className="fixed top-4 right-4 z-[150] px-4 py-1.5 rounded-full text-sm font-medium text-white hover:text-white transition font-caudex"
        style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.25)' }}
      >
        Skip →
      </button>

      <div className="absolute inset-x-0 bottom-16 flex justify-center pointer-events-none px-4">
        <div
          className="text-white text-base sm:text-lg font-caudex px-5 py-2.5 rounded-full animate-pulse"
          style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.25)' }}
        >
          {current < SLIDES.length - 1 ? 'Click to continue' : 'Click to begin'}
        </div>
      </div>
      <style>{`
        @media (orientation: portrait) {
          .opening-caption-wrapper {
            top: 50% !important;
            transform: translateY(-50%);
          }
          .opening-caption-text {
            font-size: clamp(30px, 8vw, 42px) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default GameOpening;